/**
 * Core types for the AI Framework
 */

export interface ProviderConfig {
  provider?: string;
  apiKey?: string;
  baseUrl?: string;
  timeout?: number;
  maxRetries?: number;
  privyToken?: string;
  [key: string]: any;
}

export interface UsageStats {
  tokensUsed?: number;
  requestsCount?: number;
  cost?: number;
  remaining?: number;
}

export type ProviderType = 'text' | 'tts' | 'image' | 'embedding';

export interface AIProvider {
  id: string;
  name: string;
  type: ProviderType;
  initialize(config: ProviderConfig): Promise<void>;
  isAvailable(): Promise<boolean>;
  getUsage(): Promise<UsageStats>;
}

// Text Generation Types
export interface TextGenerationOptions {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  systemPrompt?: string;
  stop?: string[];
  stream?: boolean;
}

export interface TextResponse {
  text: string;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  finishReason?: string;
}

// TTS Types
export interface TTSOptions {
  voice?: string;
  model?: string;
  speed?: number;
  pitch?: number;
  volume?: number;
  emotion?: string;
  format?: AudioFormat;
  sampleRate?: number;
}

export type AudioFormat = 'mp3' | 'wav' | 'ogg' | 'aac' | 'flac' | 'opus';

export interface Voice {
  id: string;
  name: string;
  gender?: 'male' | 'female' | 'neutral';
  language?: string;
  previewUrl?: string;
  labels?: Record<string, string>;
}

export interface AudioResponse {
  audio: ArrayBuffer;
  format: AudioFormat;
  duration?: number;
  voice?: string;
}

// Image Generation Types
export interface ImageGenerationOptions {
  model?: string;
  size?: string;
  quality?: 'standard' | 'hd';
  style?: string;
  n?: number;
}

export interface ImageResponse {
  images: Array<{
    url?: string;
    base64?: string;
    revisedPrompt?: string;
  }>;
  model: string;
}

// Conversation Types
export interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string;
  timestamp?: Date;
  metadata?: Record<string, any>;
}

export interface ConversationContext {
  conversationId: string;
  userId: string;
  agentId?: string;
  messages: Message[];
  metadata?: Record<string, any>;
}

export interface PersonalityProfile {
  name: string;
  description: string;
  traits: string[];
  tone: 'friendly' | 'professional' | 'casual' | 'humorous' | 'serious';
  guidelines: string[];
  examples?: Array<{
    input: string;
    output: string;
  }>;
}

// Response Types
export interface AIResponse {
  text: string;
  audio?: ArrayBuffer;
  audioUrl?: string;
  emotion?: string;
  actions?: AgentAction[];
  metadata?: Record<string, any>;
}

export interface AgentAction {
  type:
    | 'animation'
    | 'expression'
    | 'sound'
    | 'custom'
    | 'vrm'
    | 'camera'
    | 'canvas'
    | 'tip'
    | 'audio';
  value: string | number | { x: number; y: number; z: number };
  duration?: number;
  delay?: number;
  action?: string;
  intensity?: number;
  blendMode?: string;
  easing?: string;
  position?: { x: number; y: number };
  size?: { width: number; height: number };
  amount?: number;
  currency?: string;
  animation?: string;
  volume?: number;
  fadeIn?: number;
  fadeOut?: number;
}

// Error Types
export class AIFrameworkError extends Error {
  constructor(
    message: string,
    public code: string,
    public provider?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'AIFrameworkError';
  }
}

export class ProviderError extends AIFrameworkError {
  constructor(message: string, provider: string, details?: any) {
    super(message, 'PROVIDER_ERROR', provider, details);
    this.name = 'ProviderError';
  }
}

export class ConfigurationError extends AIFrameworkError {
  constructor(message: string, details?: any) {
    super(message, 'CONFIGURATION_ERROR', undefined, details);
    this.name = 'ConfigurationError';
  }
}

export class RateLimitError extends AIFrameworkError {
  constructor(message: string, provider: string, retryAfter?: number) {
    super(message, 'RATE_LIMIT_ERROR', provider, { retryAfter });
    this.name = 'RateLimitError';
  }
}
